import { httpsCallable, getFunctions } from 'firebase/functions';
import { serverTimestamp } from 'firebase/firestore';
import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';

// Get the correct functions region based on environment
const getFunctionsRegion = () => {
  const region = import.meta.env.VITE_REGION || 'au';
  return region === 'eu' ? 'europe-west4' : 'australia-southeast1';
};

interface ApiResponse<T> {
  statusCode: number;
  data: T;
  message?: string;
}

/**
 * Onboarding form data structure from OnboardingFlow component
 */
export interface OnboardingFormData {
  // Personal Details
  yourName: string;
  email: string;
  password: string;
  phoneNumber?: string;
  
  // Business Details
  companyName: string;
  website: string;
  teamSize: string;
  companyPlan: string;
  selectedFrameworks: string[];
  moduleLevels?: Record<string, any>; // Module-specific level configurations
  
  // Enterprise Choice (for enterprise plans)
  enterpriseChoice?: 'meeting' | 'payment';
  
  // Meeting/Billing (conditional)
  meetingDate?: string;
  meetingTime?: string;
  meetingTimeZone?: string;
  meetingPreferences?: string;
  nameOnCard?: string;
  cardValid?: boolean;
  saveCard?: boolean;
  
  // Payment information
  paymentIntentId?: string;
}

/**
 * Processed onboarding data for Firestore
 */
export interface ProcessedOnboardingData {
  customerID: string;
  ownerEmail: string;
  ownerName: string;
  casualCompanyName: string;
  fullCompanyName: string;
  orgID: string;
  licensesUsed: number;
  businessUnitNames: Record<string, string>;
  teamNames: Record<string, string>;
  users: string[];
  owner: string; // ownerUid
  
  // Additional metadata
  website: string;
  teamSize: string;
  companyPlan: string;
  selectedFrameworks: string[];
  moduleLevels?: Record<string, any>; // Module-specific level configurations
  createdAt: any; // Firestore timestamp
  
  // Meeting/billing info
  meetingScheduled?: {
    date: string;
    time: string;
    timeZone: string;
    preferences: string;
  };
  billingInfo?: {
    nameOnCard: string;
    saveCard: boolean;
    paymentIntentId?: string;
  };
}

/**
 * Progress update from Cloud Function
 */
export interface ProgressUpdate {
  stage: string;
  progress: number;
  message: string;
  timestamp: any;
}

/**
 * Tenancy creation result
 */
export interface TenancyCreationResult {
  success: boolean;
  customerID?: string;
  message?: string;
  error?: string;
  progress?: ProgressUpdate[];
}

/**
 * Service for handling tenancy creation workflow after onboarding completion
 */
export class TenancyCreatorService {
  private functions = getFunctions(undefined, getFunctionsRegion());
  
  /**
   * Create tenancy after onboarding form completion
   */
  async createTenancy(
    formData: OnboardingFormData, 
    ownerUid: string,
    onProgress?: (progress: ProgressUpdate) => void
  ): Promise<TenancyCreationResult> {
    try {
      // Step 1: Generate customer ID and process form data
      const customerID = await this.generateCustomerID(formData.companyName);
      console.log('Generated customer ID:', customerID);
      const processedData = this.processOnboardingData(formData, ownerUid, customerID);
      
      onProgress?.({
        stage: 'data-processing',
        progress: 10,
        message: 'Processing onboarding data...',
        timestamp: serverTimestamp()
      });

      // Step 2: Save onboarding data to Firestore
      await this.saveOnboardingData(customerID, processedData);
      
      onProgress?.({
        stage: 'onboarding-save',
        progress: 30,
        message: 'Onboarding data saved successfully',
        timestamp: serverTimestamp()
      });

      // Step 3: Trigger tenancy creator Cloud Function (fire and forget)
      const tenancyCreator = httpsCallable(this.functions, 'tenancyCreator');
      
      onProgress?.({
        stage: 'tenancy-creation-start',
        progress: 40,
        message: 'Starting tenancy creation process...',
        timestamp: serverTimestamp()
      });

      // Start the Cloud Function without waiting for it to complete
      tenancyCreator({ customerID }).catch(error => {
        console.error('Cloud function error:', error);
      });
      
      // Return immediately with customerID so polling can start
      return {
        success: true,
        customerID: customerID,
        message: 'Tenancy creation started',
        progress: []
      };
      
    } catch (error) {
      console.error('Tenancy creation failed:', error);
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      
      return {
        success: false,
        error: `Failed to create tenancy: ${errorMessage}`
      };
    }
  }

  /**
   * Get tenancy creation status from backend
   */
  async getTenancyStatus(customerID: string): Promise<TenancyCreationResult> {
    try {
      const getTenancyStatusFunction = httpsCallable(this.functions, 'getTenancyStatus');
      const result = await getTenancyStatusFunction({ customerID });
      
      return result.data as TenancyCreationResult;
    } catch (error) {
      console.error('Failed to get tenancy status:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get tenancy status',
        progress: []
      };
    }
  }

  /**
   * Generate a unique customer ID via Thalassa API
   */
  private async generateCustomerID(companyName: string): Promise<string> {
    try {
      const response = await deiterateAPI.POST<ApiResponse<{ customerID: string }>>(
        `/v1/tenancy/generate-id`,
        { body: JSON.stringify({ companyName }), backend: 'thalassa' }
      );
      return response.data.customerID;
    } catch (error) {
      // Fallback to client-side generation if API fails
      const timestamp = Date.now().toString(36);
      const random = Math.random().toString(36).substring(2, 8);
      const prefix = companyName
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '')
        .substring(0, 4);
      return prefix ? `${prefix}-${timestamp}${random}` : `${timestamp}${random}`;
    }
  }

  /**
   * Process onboarding form data into the expected Firestore structure
   */
  private processOnboardingData(
    formData: OnboardingFormData, 
    ownerUid: string, 
    customerID: string
  ): ProcessedOnboardingData {
    // Parse team size to number for licensesUsed
    const licensesUsed = this.parseTeamSize(formData.teamSize);
    
    // Create default business units and teams
    const businessUnitNames = {
      'default': 'Default Business Unit'
    };
    
    const teamNames = {
      'management': 'Management Team',
      'compliance': 'Compliance Team'
    };

    const processedData: ProcessedOnboardingData = {
      // Required fields for tenancy creator
      customerID,
      ownerEmail: formData.email,
      ownerName: formData.yourName,
      casualCompanyName: formData.companyName,
      fullCompanyName: formData.companyName,
      orgID: customerID,
      licensesUsed,
      businessUnitNames,
      teamNames,
      users: [ownerUid],
      owner: ownerUid,
      
      // Additional metadata
      website: formData.website,
      teamSize: formData.teamSize,
      companyPlan: formData.companyPlan,
      selectedFrameworks: formData.selectedFrameworks,
      moduleLevels: formData.moduleLevels || {}, // Include module level configurations
      createdAt: serverTimestamp()
    };

    // Add meeting info for enterprise plans
    if (formData.companyPlan === 'enterprise' && formData.meetingDate) {
      processedData.meetingScheduled = {
        date: formData.meetingDate,
        time: formData.meetingTime || '',
        timeZone: formData.meetingTimeZone || '',
        preferences: formData.meetingPreferences || ''
      };
    }
    
    // Add billing info for non-enterprise plans or enterprise with payment choice
    if ((formData.companyPlan !== 'enterprise' || formData.enterpriseChoice === 'payment') && formData.nameOnCard) {
      processedData.billingInfo = {
        nameOnCard: formData.nameOnCard,
        saveCard: formData.saveCard || false,
        paymentIntentId: formData.paymentIntentId
      };
    }

    return processedData;
  }

  /**
   * Parse team size string to number for licenses
   */
  private parseTeamSize(teamSize: string): number {
    switch (teamSize) {
      case '1-1':
        return 1;
      case '2-10':
        return 5; // Conservative estimate
      case '10-50':
        return 25; // Conservative estimate
      case '50+':
        return 50; // Conservative estimate
      default:
        return 5; // Default fallback
    }
  }

  /**
   * Save onboarding data via Thalassa API
   */
  private async saveOnboardingData(customerID: string, data: ProcessedOnboardingData): Promise<void> {
    await deiterateAPI.POST(
      `/v1/tenancy/onboarding`,
      { body: JSON.stringify({ ...data, customerID }), backend: 'thalassa' }
    );
  }

  /**
   * Check if onboarding data exists for a customer via Thalassa API
   */
  async getOnboardingData(customerID: string): Promise<ProcessedOnboardingData | null> {
    try {
      const response = await deiterateAPI.GET<ApiResponse<ProcessedOnboardingData | null>>(
        `/v1/tenancy/onboarding/${encodeURIComponent(customerID)}`,
        { backend: 'thalassa' }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get onboarding data:', error);
      return null;
    }
  }


  /**
   * Validate onboarding form data before processing
   */
  validateFormData(formData: OnboardingFormData): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Required fields validation
    if (!formData.yourName?.trim()) errors.push('Name is required');
    if (!formData.email?.trim()) errors.push('Email is required');
    if (!formData.password?.trim()) errors.push('Password is required');
    if (!formData.companyName?.trim()) errors.push('Company name is required');
    if (!formData.website?.trim()) errors.push('Website is required');
    if (!formData.teamSize) errors.push('Team size is required');
    if (!formData.companyPlan) errors.push('Company plan is required');
    if (!formData.selectedFrameworks?.length) errors.push('At least one framework must be selected');

    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.push('Invalid email format');
    }

    // Enterprise plan validation
    if (formData.companyPlan === 'enterprise') {
      console.log('Enterprise plan selected', formData);
      if (!formData.enterpriseChoice) {
        errors.push('Please select either meeting scheduling or immediate payment');
      } else if (formData.enterpriseChoice === 'meeting') {
        console.log('Meeting scheduling selected', formData);
        if (!formData.meetingDate) errors.push('Meeting date is required for enterprise plan');
        if (!formData.meetingTime) errors.push('Meeting time is required for enterprise plan');
      } else if (formData.enterpriseChoice === 'payment') {
        console.log('Payment selected', formData);
        // Enterprise immediate payment validation
        if (!formData.nameOnCard?.trim()) errors.push('Name on card is required');
        if (!formData.cardValid) errors.push('Valid card details are required');
      }
    } else {
      // Non-enterprise plan validation
      if (!formData.nameOnCard?.trim()) errors.push('Name on card is required');
      if (!formData.cardValid) errors.push('Valid card details are required');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }
}

// Export singleton instance
export const tenancyCreatorService = new TenancyCreatorService();
